import React, { useEffect, useState } from 'react';
import {
  MessageCircle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  EyeOff,
  Send,
  Copy,
  Check
} from 'lucide-react';
import { whatsappService, ConfiguracaoWhatsapp, NotificacaoTemplate, NotificacaoLog } from '../../services/whatsappService';

const STATUS_LABEL: Record<string, { texto: string; cor: string }> = {
  enviando: { texto: 'Enviando', cor: 'bg-slate-100 text-slate-600' },
  enviado: { texto: 'Enviado', cor: 'bg-blue-100 text-blue-700' },
  entregue: { texto: 'Entregue', cor: 'bg-emerald-100 text-emerald-700' },
  lido: { texto: 'Lido', cor: 'bg-emerald-100 text-emerald-700' },
  erro: { texto: 'Erro', cor: 'bg-red-100 text-red-700' },
  bloqueado_sem_consentimento: { texto: 'Sem consentimento do cliente', cor: 'bg-amber-100 text-amber-700' },
};

function CampoCopiavel({ label, valor }: { label: string; valor: string }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      /* clipboard indisponível, ignora silenciosamente */
    }
  };
  return (
    <div>
      <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 font-mono text-slate-700 overflow-x-auto whitespace-nowrap">
          {valor}
        </code>
        <button
          type="button"
          onClick={copiar}
          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 shrink-0 cursor-pointer"
          title="Copiar"
        >
          {copiado ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export function ConfiguracaoWhatsAppSection() {
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'sucesso' | 'erro'; mensagem: string } | null>(null);
  const [guiaAberto, setGuiaAberto] = useState(false);

  const [config, setConfig] = useState<ConfiguracaoWhatsapp | null>(null);
  const [ativo, setAtivo] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [businessAccountId, setBusinessAccountId] = useState('');
  const [numeroExibicao, setNumeroExibicao] = useState('');
  const [webhookVerifyToken, setWebhookVerifyToken] = useState('');
  const [urlPublicaApp, setUrlPublicaApp] = useState('');

  const [accessToken, setAccessToken] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [mostrarAccessToken, setMostrarAccessToken] = useState(false);
  const [mostrarAppSecret, setMostrarAppSecret] = useState(false);

  const [templates, setTemplates] = useState<NotificacaoTemplate[]>([]);
  const [templateEmEdicao, setTemplateEmEdicao] = useState<Record<string, { template_name: string; idioma: string; ativo: boolean }>>({});
  const [salvandoTemplate, setSalvandoTemplate] = useState<string | null>(null);

  const [logs, setLogs] = useState<NotificacaoLog[]>([]);
  const [reenviandoId, setReenviandoId] = useState<number | null>(null);

  const webhookUrl = 'https://ztnsovldwkbddnuvpxfy.supabase.co/functions/v1/whatsapp-webhook';

  const showFeedback = (tipo: 'sucesso' | 'erro', mensagem: string) => {
    setFeedback({ tipo, mensagem });
    setTimeout(() => setFeedback(null), 5000);
  };

  const carregarTudo = async () => {
    setLoading(true);
    try {
      const [cfg, tpls, lgs] = await Promise.all([
        whatsappService.getConfiguracao(),
        whatsappService.getTemplates(),
        whatsappService.getLogs(30),
      ]);
      setConfig(cfg);
      setAtivo(cfg.ativo);
      setPhoneNumberId(cfg.phone_number_id || '');
      setBusinessAccountId(cfg.business_account_id || '');
      setNumeroExibicao(cfg.numero_exibicao || '');
      setWebhookVerifyToken(cfg.webhook_verify_token || '');
      setUrlPublicaApp(cfg.url_publica_app || '');
      setTemplates(tpls);
      const inicial: Record<string, { template_name: string; idioma: string; ativo: boolean }> = {};
      tpls.forEach((t) => {
        inicial[t.evento_codigo] = { template_name: t.template_name || '', idioma: t.idioma || 'pt_BR', ativo: t.ativo };
      });
      setTemplateEmEdicao(inicial);
      setLogs(lgs);
      if (!cfg.phone_number_id) setGuiaAberto(true);
    } catch (e: any) {
      showFeedback('erro', e.message || 'Falha ao carregar configuração do WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarTudo();
  }, []);

  const gerarTokenAleatorio = () => {
    const token = Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    setWebhookVerifyToken(token);
  };

  const handleSalvarConfiguracao = async () => {
    setSalvando(true);
    try {
      await whatsappService.salvarConfiguracao({
        ativo,
        phone_number_id: phoneNumberId.trim(),
        business_account_id: businessAccountId.trim(),
        numero_exibicao: numeroExibicao.trim(),
        webhook_verify_token: webhookVerifyToken.trim(),
        url_publica_app: urlPublicaApp.trim(),
      });

      if (accessToken.trim()) {
        await whatsappService.salvarSegredo('access_token', accessToken.trim());
        setAccessToken('');
      }
      if (appSecret.trim()) {
        await whatsappService.salvarSegredo('app_secret', appSecret.trim());
        setAppSecret('');
      }

      showFeedback('sucesso', 'Configuração do WhatsApp salva com sucesso!');
      await carregarTudo();
    } catch (e: any) {
      showFeedback('erro', e.message || 'Erro ao salvar configuração.');
    } finally {
      setSalvando(false);
    }
  };

  const handleTestarConexao = async () => {
    setTestando(true);
    try {
      const res = await whatsappService.testarConexao();
      if (res.sucesso) {
        showFeedback('sucesso', `${res.mensagem}${res.numero ? ` Número: ${res.numero}` : ''}${res.nome_verificado ? ` (${res.nome_verificado})` : ''}`);
      } else {
        showFeedback('erro', res.mensagem);
      }
    } catch (e: any) {
      showFeedback('erro', e.message || 'Erro ao testar conexão.');
    } finally {
      setTestando(false);
    }
  };

  const handleSalvarTemplate = async (evento_codigo: string) => {
    const dados = templateEmEdicao[evento_codigo];
    if (!dados) return;
    setSalvandoTemplate(evento_codigo);
    try {
      await whatsappService.salvarTemplate(evento_codigo, dados);
      showFeedback('sucesso', 'Modelo de mensagem salvo.');
      await carregarTudo();
    } catch (e: any) {
      showFeedback('erro', e.message || 'Erro ao salvar modelo.');
    } finally {
      setSalvandoTemplate(null);
    }
  };

  const handleReenviar = async (logId: number) => {
    setReenviandoId(logId);
    try {
      const res = await whatsappService.reenviarNotificacao(logId);
      showFeedback(res.sucesso ? 'sucesso' : 'erro', res.mensagem);
      await carregarTudo();
    } catch (e: any) {
      showFeedback('erro', e.message || 'Erro ao reenviar notificação.');
    } finally {
      setReenviandoId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {feedback && (
        <div className={`p-3.5 rounded-xl border flex items-start gap-3 text-sm font-medium ${
          feedback.tipo === 'sucesso' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {feedback.tipo === 'sucesso' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
          <span>{feedback.mensagem}</span>
        </div>
      )}

      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm border border-slate-700/50">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-black">Notificações via WhatsApp (Meta Cloud API)</h2>
            <p className="text-slate-400 text-xs mt-0.5">Avisa automaticamente o cliente pelo WhatsApp oficial em eventos como OS confirmada, em rota, pronta para assinatura, finalizada, cancelada ou fatura disponível — só para quem deu consentimento.</p>
          </div>
        </div>
        <div className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shrink-0 ${
          ativo && config?.possui_access_token ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-400/30' : 'bg-slate-700/50 text-slate-400 border border-slate-600/50'
        }`}>
          {ativo && config?.possui_access_token ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {ativo && config?.possui_access_token ? 'Ativo' : 'Inativo'}
        </div>
      </div>

      {/* Guia de configuração no Meta */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <button
          type="button"
          onClick={() => setGuiaAberto((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 cursor-pointer"
        >
          <span className="text-sm font-bold text-slate-800">Como configurar no Meta (passo a passo)</span>
          {guiaAberto ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {guiaAberto && (
          <div className="px-5 pb-5 space-y-4 text-sm text-slate-600 border-t border-slate-100 pt-4">
            <ol className="space-y-3 list-decimal list-inside">
              <li>
                Acesse <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold inline-flex items-center gap-1">business.facebook.com <ExternalLink className="w-3 h-3" /></a> e crie (ou use) uma conta do Business Manager com o CNPJ da empresa.
              </li>
              <li>
                Em <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold inline-flex items-center gap-1">developers.facebook.com/apps <ExternalLink className="w-3 h-3" /></a>, clique em "Criar App" → tipo "Empresa" → adicione o produto <strong>WhatsApp</strong>.
              </li>
              <li>
                No painel do produto WhatsApp, em "Introdução"/"Primeiros Passos", você já tem um número de teste. Para produção, cadastre o número de telefone real da empresa (WhatsApp &gt; Configuração da API &gt; Adicionar número de telefone).
              </li>
              <li>
                Copie o <strong>Phone Number ID</strong> (ID do número de telefone) e o <strong>WhatsApp Business Account ID</strong> exibidos na página "Primeiros Passos" — cole nos campos abaixo.
              </li>
              <li>
                Gere um <strong>Access Token permanente</strong>: vá em Configurações da Empresa → Usuários do Sistema → crie um Usuário do Sistema com papel "Admin" → gere um token com as permissões <code className="bg-slate-100 px-1 rounded">whatsapp_business_messaging</code> e <code className="bg-slate-100 px-1 rounded">whatsapp_business_management</code>, sem data de expiração. Cole no campo "Access Token" abaixo.
              </li>
              <li>
                Crie um <strong>Modelo de Mensagem</strong> (Template) para cada evento que quiser usar, em WhatsApp Manager → Modelos de Mensagem → categoria <em>Utility</em> (mensagens sobre um pedido/serviço já existente do cliente não são "Marketing"). Todos os eventos abaixo seguem o mesmo padrão de 3 variáveis posicionais: <code className="bg-slate-100 px-1 rounded">{'{{1}}'}</code> = nome do cliente, <code className="bg-slate-100 px-1 rounded">{'{{2}}'}</code> = número da OS, <code className="bg-slate-100 px-1 rounded">{'{{3}}'}</code> = link do Painel do Cliente. Exemplo para "OS pronta para assinatura":
                <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono whitespace-pre-wrap">
{`Olá {{1}}! Sua Ordem de Serviço nº {{2}} está pronta para assinatura.
Acesse o link para visualizar e assinar: {{3}}

Se precisar acessar novamente, entre pelo Painel do Cliente com seu login e senha.`}
                </div>
                Depois de aprovado cada template, copie o <strong>nome exato</strong> (ex.: <code className="bg-slate-100 px-1 rounded">os_pronta_assinatura</code>) e cole no evento correspondente na seção "Eventos e Modelos" abaixo — cada evento tem seu próprio interruptor de ativo/inativo, então você pode ligar só os que já tiver template aprovado.
              </li>
              <li>
                A Meta exige <strong>consentimento (opt-in)</strong> do cliente antes de enviar qualquer mensagem de template — não é opcional, é política da plataforma. No cadastro de cada cliente (Pessoas → editar), marque "Aceita receber notificações via WhatsApp" apenas com autorização dele. Sem essa marcação, o sistema nunca envia nada para aquele cliente, mesmo com o evento ativo.
              </li>
              <li>
                (Opcional, mas recomendado) Em Configurações do App → Básico, copie o <strong>App Secret</strong> e cole abaixo — ele é usado para validar que os retornos de status (entregue/lido) realmente vêm da Meta.
              </li>
              <li>
                Em WhatsApp → Configuração → Webhooks, clique em "Editar" e cole:
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <CampoCopiavel label="URL de retorno de chamada" valor={webhookUrl} />
                  <CampoCopiavel label="Token de verificação" valor={webhookVerifyToken || '(gere um abaixo e salve antes)'} />
                </div>
                Depois clique em "Gerenciar" e assine o campo <code className="bg-slate-100 px-1 rounded">messages</code>.
              </li>
              <li>
                Preencha a <strong>URL pública do sistema</strong> abaixo (ex.: <code className="bg-slate-100 px-1 rounded">https://app.suaempresa.com.br</code>) — é o endereço usado para montar o link enviado ao cliente.
              </li>
              <li>
                Marque "Ativo", salve e use o botão <strong>"Testar Conexão"</strong> para confirmar que tudo está correto.
              </li>
            </ol>
            <p className="text-3xs text-slate-400 pt-2 border-t border-slate-100">
              Atenção: a Meta cobra por conversa iniciada pela empresa após a franquia gratuita mensal, e o WhatsApp exige que toda mensagem iniciada pela empresa (fora da janela de 24h de atendimento) use um template aprovado — não é possível enviar texto livre neste tipo de notificação automática.
            </p>
          </div>
        )}
      </div>

      {/* Conexão com a Meta */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Conexão com a Meta</h3>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
            <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
            Notificações ativas
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Phone Number ID</label>
            <input
              value={phoneNumberId}
              onChange={(e) => setPhoneNumberId(e.target.value)}
              placeholder="Ex: 109876543210987"
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">WhatsApp Business Account ID</label>
            <input
              value={businessAccountId}
              onChange={(e) => setBusinessAccountId(e.target.value)}
              placeholder="Ex: 123456789012345"
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Número exibido (informativo)</label>
            <input
              value={numeroExibicao}
              onChange={(e) => setNumeroExibicao(e.target.value)}
              placeholder="Ex: +55 11 99999-9999"
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">URL pública do sistema</label>
            <input
              value={urlPublicaApp}
              onChange={(e) => setUrlPublicaApp(e.target.value)}
              placeholder="https://app.suaempresa.com.br"
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">
              Access Token {config?.possui_access_token && <span className="text-emerald-600 normal-case font-semibold">(configurado — deixe em branco para manter)</span>}
            </label>
            <div className="relative">
              <input
                type={mostrarAccessToken ? 'text' : 'password'}
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder={config?.possui_access_token ? '••••••••••••••••••••' : 'Cole o Access Token permanente'}
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 pr-10 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
              />
              <button type="button" onClick={() => setMostrarAccessToken((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer">
                {mostrarAccessToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">
              App Secret (opcional) {config?.possui_app_secret && <span className="text-emerald-600 normal-case font-semibold">(configurado)</span>}
            </label>
            <div className="relative">
              <input
                type={mostrarAppSecret ? 'text' : 'password'}
                value={appSecret}
                onChange={(e) => setAppSecret(e.target.value)}
                placeholder={config?.possui_app_secret ? '••••••••••••••••••••' : 'Usado para validar o webhook'}
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 pr-10 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
              />
              <button type="button" onClick={() => setMostrarAppSecret((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer">
                {mostrarAppSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Token de verificação do Webhook</label>
            <div className="flex items-center gap-2">
              <input
                value={webhookVerifyToken}
                onChange={(e) => setWebhookVerifyToken(e.target.value)}
                placeholder="Gere um token abaixo"
                className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2.5 font-mono focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
              />
              <button
                type="button"
                onClick={gerarTokenAleatorio}
                className="px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 whitespace-nowrap cursor-pointer"
              >
                Gerar novo
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={handleSalvarConfiguracao}
            disabled={salvando}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-900/20 transition-all disabled:opacity-50 cursor-pointer"
          >
            {salvando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {salvando ? 'Salvando...' : 'Salvar Configuração'}
          </button>
          <button
            type="button"
            onClick={handleTestarConexao}
            disabled={testando}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition-all disabled:opacity-50 cursor-pointer"
          >
            {testando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            {testando ? 'Testando...' : 'Testar Conexão'}
          </button>
        </div>
      </div>

      {/* Eventos e Modelos de Mensagem */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-800">Eventos e Modelos de Mensagem (Templates aprovados)</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Cada evento é disparado automaticamente pela mudança de status da OS. O interruptor "Ativo" liga/desliga o envio desse evento — mesmo ativo, só é enviado a clientes com consentimento marcado no cadastro (Pessoas).
          </p>
        </div>
        <div className="divide-y divide-slate-100">
          {templates.map((t) => {
            const edit = templateEmEdicao[t.evento_codigo] || { template_name: '', idioma: 'pt_BR', ativo: true };
            return (
              <div key={t.evento_codigo} className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-800">{t.descricao}</p>
                    {t.variaveis_ajuda && <p className="text-3xs text-slate-400 mt-0.5">{t.variaveis_ajuda}</p>}
                  </div>
                  <span className={`px-2.5 py-1 rounded-lg text-3xs font-bold shrink-0 ${
                    edit.template_name ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {edit.template_name ? 'Configurado' : 'Pendente'}
                  </span>
                </div>
                <div className="grid gap-3 sm:grid-cols-[2fr_1fr_auto_auto] items-end">
                  <div>
                    <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Nome do template aprovado</label>
                    <input
                      value={edit.template_name}
                      onChange={(e) => setTemplateEmEdicao((s) => ({ ...s, [t.evento_codigo]: { ...edit, template_name: e.target.value } }))}
                      placeholder="ex: os_pronta_assinatura"
                      className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 font-mono focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Idioma</label>
                    <input
                      value={edit.idioma}
                      onChange={(e) => setTemplateEmEdicao((s) => ({ ...s, [t.evento_codigo]: { ...edit, idioma: e.target.value } }))}
                      placeholder="pt_BR"
                      className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer pb-2.5">
                    <input
                      type="checkbox"
                      checked={edit.ativo}
                      onChange={(e) => setTemplateEmEdicao((s) => ({ ...s, [t.evento_codigo]: { ...edit, ativo: e.target.checked } }))}
                      className="w-4 h-4 accent-emerald-600"
                    />
                    Ativo
                  </label>
                  <button
                    type="button"
                    onClick={() => handleSalvarTemplate(t.evento_codigo)}
                    disabled={salvandoTemplate === t.evento_codigo}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {salvandoTemplate === t.evento_codigo ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Histórico de Notificações */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Histórico de Notificações Enviadas</h3>
          <button type="button" onClick={carregarTudo} className="text-slate-400 hover:text-slate-600 cursor-pointer" title="Atualizar">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {logs.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">Nenhuma notificação enviada ainda.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-3xs">
                <tr>
                  <th className="text-left px-4 py-2.5 font-bold">Data</th>
                  <th className="text-left px-4 py-2.5 font-bold">Evento</th>
                  <th className="text-left px-4 py-2.5 font-bold">OS</th>
                  <th className="text-left px-4 py-2.5 font-bold">Telefone</th>
                  <th className="text-left px-4 py-2.5 font-bold">Status</th>
                  <th className="text-left px-4 py-2.5 font-bold">Detalhe</th>
                  <th className="text-right px-4 py-2.5 font-bold">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => {
                  const st = STATUS_LABEL[log.status] || { texto: log.status, cor: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString('pt-BR')}</td>
                      <td className="px-4 py-2.5 text-slate-700">{log.evento_codigo}</td>
                      <td className="px-4 py-2.5 text-slate-700">{log.os_id ? `#${log.os_id}` : '-'}</td>
                      <td className="px-4 py-2.5 text-slate-500 font-mono">{log.telefone || '-'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-md text-3xs font-bold ${st.cor}`}>{st.texto}</span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-400 max-w-xs truncate" title={log.erro_detalhe || ''}>{log.erro_detalhe || '-'}</td>
                      <td className="px-4 py-2.5 text-right">
                        {log.status === 'erro' && (
                          <button
                            type="button"
                            onClick={() => handleReenviar(log.id)}
                            disabled={reenviandoId === log.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-3xs font-bold text-slate-600 disabled:opacity-50 cursor-pointer"
                          >
                            {reenviandoId === log.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Reenviar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
